import React, { useCallback, useState, useMemo } from 'react'
import PropTypes from 'prop-types'
import { i18n } from '@fs/zion-locale'
import { Checkbox, Separator, Button, colors, Row, Divider } from '@fs/zion-ui'
import { ControlZoomIn, NoticeWarning } from '@fs/zion-icon'
import { DateSuggest } from '@fs/zion-standards'
import { v4 as uuid } from 'uuid'

import { useCoverages, useGroupData } from '../../../../hooks'
import { formatWithRules, formatDate } from '../../../../../../../shared/utils/dateUtils'
import getFormattedDate, { dateFormats } from '../../../../../../../shared/utils/dateFormatHelper'
import styles from './EditableCoverageDate.styles'
import transl from './EditableCoverageDate.transl'
import {
  validateDateRangeAgainstPlace,
  getPlaceInfoById,
  getPlaceDateRangeFromCoverages,
} from './dateRangeValidationUtils'

export function DateValidationHelpMessage({ validation }) {
  if (!validation) return null
  if (validation.allOutside) {
    return (
      <span css={styles.helpMessage}>
        <NoticeWarning /> {transl.dateRangeEntirelyOutside()}
      </span>
    )
  }
  if (validation.partiallyOutside) {
    return (
      <span css={styles.helpMessage}>
        <NoticeWarning /> {transl.dateRangePartiallyOutside()}
      </span>
    )
  }
  return null
}

DateValidationHelpMessage.propTypes = {
  validation: PropTypes.shape({
    allOutside: PropTypes.bool,
    partiallyOutside: PropTypes.bool,
  }),
}

export const DateCoverageSuggestions = ({
  approximateYear = null,
  onDateSuggestionSelected = () => {},
  setApproximateYear = () => {},
  originalDateIndex,
}) => {
  const dividerSubtitle = () => i18n.t('editableCoverage.dividerSubtitle', 'Suggested Date Ranges')

  const createDateRange = useCallback(
    (offset = 1) => {
      if (!approximateYear?.year) return ''

      const fromYear = approximateYear.year - offset
      const toYear = approximateYear.year + offset

      const fromDate = new Date(fromYear, 0, 1)
      fromDate.setFullYear(fromYear)
      const toDate = new Date(toYear, 11, 31)
      toDate.setFullYear(toYear)

      const format = 'YEAR'
      const locale = i18n.language === 'zh' ? 'zh-Hant' : i18n.language

      const formattedFromYear = formatDate({ date: fromDate, format, locale })
      const formattedToYear = formatDate({ date: toDate, format, locale })

      return {
        text: `${formattedFromYear}-${formattedToYear}`,
        startDate: fromDate,
        endDate: toDate,
      }
    },
    [approximateYear]
  )

  const oneYearRange = useMemo(() => createDateRange(1), [createDateRange])
  const threeYearRange = useMemo(() => createDateRange(3), [createDateRange])
  const fiveYearRange = useMemo(() => createDateRange(5), [createDateRange])
  const decadeRange = useMemo(() => {
    if (!approximateYear?.year) return ''

    const decadeStart = Math.floor(approximateYear.year / 10) * 10
    const decadeEnd = decadeStart + 9

    // NOTE: BC dates are offset by 1 year
    const fromDate = new Date(decadeStart, 0, 1)
    fromDate.setFullYear(decadeStart < 0 ? decadeStart + 1 : decadeStart)
    const toDate = new Date(decadeEnd, 11, 31)
    toDate.setFullYear(decadeEnd < 0 ? decadeEnd + 1 : decadeEnd)

    const format = 'YEAR'
    const locale = i18n.language === 'zh' ? 'zh-Hant' : i18n.language

    const formattedFromYear = formatDate({ date: fromDate, format, locale })
    const formattedToYear = formatDate({ date: toDate, format, locale })

    return {
      text: `${formattedFromYear}-${formattedToYear}`,
      startDate: fromDate,
      endDate: toDate,
    }
  }, [approximateYear])

  const addSuggestedYearRange = useCallback(
    (yearRange) => {
      const firstYear = yearRange.startDate.getFullYear()
      const secondYear = yearRange.endDate.getFullYear()

      const formalText = `${firstYear > -1 ? '+' : ''}${firstYear}/${secondYear > -1 ? '+' : ''}${secondYear}`

      onDateSuggestionSelected({ standard: { formalText } }, originalDateIndex)
      setApproximateYear(null)
    },
    [onDateSuggestionSelected, setApproximateYear, originalDateIndex]
  )
  const ranges = [oneYearRange, threeYearRange, fiveYearRange, decadeRange]

  return (
    <>
      <Divider aria-hidden />
      <span style={{ color: colors.gray50, fontSize: '12px' }}>{dividerSubtitle()}</span>
      <Separator size="nano" aria-hidden />
      <Row wrap gutters="xs" guttersY="xs">
        {ranges.map((r) => (
          <Button key={r.text} onClick={() => addSuggestedYearRange(r)}>
            {r.text}
          </Button>
        ))}
      </Row>
      <Separator size="xs" />
      <Divider aria-hidden />
      <Separator size="xs" aria-hidden />
    </>
  )
}

/**
 * EditableCoverageDate is a component that allows users to edit coverage dates
 * @param {isTest} bool This variable is used so that date text can be printed in a test
 */
export default function EditableCoverageDate({
  forVolumeSet,
  placeId,
  recordType,
  disabled,
  errorMessage,
  isTest,
  datesChecked,
  onDateChecked,
}) {
  const aboutMessage = transl.approximateMessage()
  const standardDateErrorMessage = transl.standardDateErrorMessage

  const { setDate } = useCoverages({ forVolumeSet })
  const { group } = useGroupData()
  const dates = recordType.date
  const coverageDates = useMemo(() => initCoverageDates(dates), [dates])
  const [approximateYear, setApproximateYear] = useState(null)
  const [inputDirty, setInputDirty] = useState(false)
  const [selectDateMessage, setSelectDateMessage] = useState(null)
  const [userEnteredText, setUserEnteredText] = useState(null)

  const groupPlaceInfo = useMemo(
    () => getPlaceInfoById(group?.placeInformation, placeId),
    [group?.placeInformation, placeId]
  )
  const coveragePlaceInfo = useMemo(
    () => getPlaceDateRangeFromCoverages(group?.coverages, placeId),
    [group?.coverages, placeId]
  )

  // Place date range comes from group.placeInformation or from coverage.place (set by place component when user selects a place)
  const placeInfo = groupPlaceInfo ?? coveragePlaceInfo ?? null

  // Check if a specific date is invalid
  const getDateValidation = useCallback(
    (coverageDate) => {
      if (!coverageDate?.value) return null
      return validateDateRangeAgainstPlace(coverageDate.value, placeInfo)
    },
    [placeInfo]
  )

  const dateSuggestHandler = useCallback(
    (newDate, originalDateIndex) => {
      if (newDate?.standard) {
        setInputDirty(false)
        setSelectDateMessage(null)
        const { formalText } = newDate.standard
        // case for "about" dates or "before" dates
        if (formalText?.startsWith('A') || formalText?.startsWith('/')) {
          // Just grab the year here. If they can be more accurate than a year range they shouldn't use "about"
          const year = Number(formalText.match(/[A/]([+-]\d+)/)[1])
          setApproximateYear({ year, originalDateIndex })
          return
        }
        // case for "after" dates
        if (formalText?.endsWith('/')) {
          const year = Number(formalText.match(/^([+-]\d+)/)[1])
          setApproximateYear({ year, originalDateIndex })
          return
        }

        if (approximateYear) setApproximateYear(null)

        const editedDate = {
          label: cleanDate(newDate, i18n.language === 'zh' ? 'zh-Hant' : i18n.language),
          value: { gedcomX: formalText },
          localKey: coverageDates[originalDateIndex]?.localKey, // preserve localKey
        }

        // edit coverage date and set dates with editted date
        const newCoverageDates = coverageDates.map((prevCoverageDate, index) => {
          if (index === originalDateIndex) return editedDate
          return prevCoverageDate
        })
        setDate(placeId, Number(recordType.id), newCoverageDates)
      } else {
        // When clearing, preserve the localKey so the input doesn't remount and lose focus
        const newCoverageDates = coverageDates.map((prevCoverageDate, index) => {
          if (index === originalDateIndex) return { localKey: prevCoverageDate?.localKey }
          return prevCoverageDate
        })
        setDate(placeId, Number(recordType.id), newCoverageDates)
      }
    },
    [approximateYear, coverageDates, placeId, recordType, setDate]
  )

  const handleInput = useCallback(
    (value) => {
      if (!inputDirty) setInputDirty(true)
      setUserEnteredText(value)
    },
    [inputDirty]
  )

  const onBlur = useCallback(() => {
    setSelectDateMessage(inputDirty && userEnteredText ? standardDateErrorMessage() : null)
  }, [inputDirty, userEnteredText])

  function addDateToEdit() {
    // add empty object with a new localKey so that a new DateSuggest is rendered and keyed
    const newCoverageDates = [...coverageDates, { localKey: uuid() }]
    setDate(placeId, Number(recordType.id), newCoverageDates)
  }

  return (
    <>
      {coverageDates.map((coverageDate, dateIndex) => {
        const dateValidation = getDateValidation(coverageDate)
        return (
          <div css={{ paddingTop: '0px' }} key={coverageDate.localKey || coverageDate.label || dateIndex}>
            <Row alignItemsY="middle">
              <Checkbox
                aria-label={transl.selectDateLabel(coverageDate.label)}
                data-testid={`EditableCoverage.DateCheckbox-${placeId}-${recordType.id}-${dateIndex}`}
                name={`date${dateIndex}`}
                checked={datesChecked?.[dateIndex]}
                onChange={({ target }) => onDateChecked(dateIndex, target.checked)}
              />

              <DateSuggest
                onSuggestionSelected={(newDate) => dateSuggestHandler(newDate, dateIndex)}
                disabled={disabled}
                placeholder={
                  coverageDates.length > 1 ? transl.addAnotherDatePlaceHolder() : transl.addDatePlaceHolder()
                }
                clearable
                errorMessage={
                  selectDateMessage ||
                  (approximateYear?.originalDateIndex === dateIndex && aboutMessage) ||
                  errorMessage
                }
                initialSelectedItem={{
                  key: coverageDate?.label || '',
                  primaryText: coverageDate?.label || '',
                }}
                onInputTextChange={handleInput}
                onBlur={onBlur}
              />
              {isTest && <div data-testid="coverageDate">{coverageDate.label}</div>}
            </Row>
            {dateValidation && <DateValidationHelpMessage validation={dateValidation} />}
            {approximateYear?.originalDateIndex === dateIndex && (
              <DateCoverageSuggestions
                onDateSuggestionSelected={dateSuggestHandler}
                approximateYear={approximateYear}
                setApproximateYear={setApproximateYear}
                originalDateIndex={dateIndex}
              />
            )}
          </div>
        )
      })}
      <Separator size="sm" aria-hidden />
      <Button onClick={addDateToEdit}>
        <ControlZoomIn /> {transl.datePlaceholder()}
      </Button>
      <Divider aria-hidden />
      <Separator size="xs" aria-hidden />
    </>
  )
}

EditableCoverageDate.propTypes = {
  errorMessage: PropTypes.string,
  forVolumeSet: PropTypes.bool,
  placeId: PropTypes.string,
  recordType: PropTypes.shape({
    id: PropTypes.number,
    name: PropTypes.string,
    date: PropTypes.arrayOf(
      PropTypes.shape({
        fromTimestamp: PropTypes.number,
        toTimestamp: PropTypes.number,
        gedcomX: PropTypes.string,
      })
    ),
  }),
}

function initCoverageDates(dates = []) {
  const initChips = dates.map((date) => {
    let formattedDate = null
    if (date.formatted) {
      formattedDate = date.formatted
    } else {
      formattedDate = getFormattedDate(date.fromTimestamp, date.toTimestamp, dateFormats.formatted)
    }
    return {
      label: String(formattedDate || ''),
      value: {
        formatted: formattedDate,
        gedcomX: date.gedcomX,
      },
      localKey: date.localKey || uuid(),
    }
  })
  return initChips
}

export function cleanDate(date, locale) {
  let formalText = date?.standard?.formalText

  if (!formalText && date?.primaryText) formalText = date.primaryText

  if (!formalText?.includes('/')) return date?.standard?.standardizedText

  // date ranges are formatted [+-]y+-m+-d+/[+-]y+-m+-d+ or [+-]y+/[+-]y+
  const [fromDateFormal, toDateFormal] = formalText.split('/')
  const fromISOString = fromDateFormal ? getDateFromFormalDate(fromDateFormal).toISOString() : null
  let toISOString

  // Year range
  if (/^[+-]?\d+\/[+-]?\d+$/.test(formalText)) {
    const toDate = getDateFromFormalDate(toDateFormal)
    toDate.setFullYear(toDate.getFullYear() + 1)
    toDate.setDate(toDate.getDate() - 1)
    toISOString = toDate.toISOString()
  } else if (toDateFormal.split('-').length > 2) toISOString = getDateFromFormalDate(toDateFormal).toISOString()
  else if (!toDateFormal) toISOString = new Date().toISOString()
  else {
    const [year, month] = toDateFormal.split('-')
    // Little trick to avoid doing the math for when a month has 31, 30, 29 or 28 days. Set the date to first of the next month.
    const newDate = new Date(parseInt(year, 10), parseInt(month, 10), 1)

    // subtract one day from that month
    newDate.setDate(newDate.getDate() - 1)

    // gives you the last day of the desired month
    toISOString = newDate.toISOString()
  }

  return formatWithRules({ fromISOString, toISOString, locale })
}

// This function gets a date object from a formal date, taking into account BC dates and years less than 100
function getDateFromFormalDate(formalDate) {
  const { day, month, year } = formalDate.match(/(?<year>[+-]\d+)-?(?<month>\d+)?-?(?<day>\d+)?/)?.groups || {}
  let date

  if (Number(year) < 100) {
    date = new Date()
    date.setDate(day || 1)
    date.setMonth(month || 0)
    date.setFullYear(Number(year))
  } else {
    const baseDate = new Date(formalDate)
    date = new Date(Date.UTC(baseDate.getFullYear(), baseDate.getMonth(), baseDate.getDate()))
  }

  return date
}
